import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface TypewriterProps {
    phrases: string[];
    typingSpeed?: number;
    deletingSpeed?: number;
    delayBetween?: number;
    cursorColor?: string;
    className?: string;
}

export const Typewriter = ({
    phrases,
    typingSpeed = 40,
    deletingSpeed = 25,
    delayBetween = 2500,
    cursorColor = "hsl(var(--primary))",
    className,
}: TypewriterProps) => {
    const [phraseIndex, setPhraseIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        const current = phrases[phraseIndex] || "";
        let timeout: ReturnType<typeof setTimeout>;

        if (!isDeleting && charIndex < current.length) {
            timeout = setTimeout(() => setCharIndex((c) => c + 1), typingSpeed);
        } else if (!isDeleting && charIndex === current.length) {
            timeout = setTimeout(() => setIsDeleting(true), delayBetween);
        } else if (isDeleting && charIndex > 0) {
            timeout = setTimeout(() => setCharIndex((c) => c - 1), deletingSpeed);
        } else if (isDeleting && charIndex === 0) {
            setIsDeleting(false);
            setPhraseIndex((i) => (i + 1) % phrases.length);
        }

        return () => clearTimeout(timeout);
    }, [charIndex, isDeleting, phraseIndex, phrases, typingSpeed, deletingSpeed, delayBetween]);

    const currentText = (phrases[phraseIndex] || "").substring(0, charIndex);

    return (
        <span className={cn("relative", className)}>
            {currentText}
            <motion.span
                animate={{ opacity: [1, 0] }}
                transition={{ repeat: Infinity, duration: 0.7 }}
                className="inline-block w-[3px] h-[0.85em] align-middle rounded-full ml-1"
                style={{ backgroundColor: cursorColor }}
            />
        </span>
    );
};
